<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Planner Headers</h5>
          <small class="text-muted">Manage planner sheet column headers</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div></div>
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ displayedRange.total }}</strong>
          </span>
        </div>
        <div></div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--search-only d-flex align-items-center">
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Header</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="filteredHeaders"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr
                v-for="n in 5"
                :key="'skeleton-' + n"
                class="skeleton-row"
              >
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>

          <template #cell(header)="data">
            <b-badge variant="light-primary">
              {{ data.value }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editHeader(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteHeader(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Planner Header' : 'Add Planner Header'"
      @hidden="resetForm"
      centered
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Title" label-for="title">
              <b-form-input
                id="title"
                v-model.trim="form.header"
                placeholder="Enter header title"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveHeader" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "PlannerHeaders",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      headers: [],
      searchQuery: "",
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      fields: [
        { label: "S.No", key: "index", thClass: "text-center", tdClass: "text-center" },
        { key: "header", label: "Header" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],
      form: {
        id: 0,
        header: "",
        isActive: true,
        sequence: 0,
        campusID: 0,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    filteredHeaders() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.headers;
      }
      const q = this.searchQuery.trim().toLowerCase();
      return this.headers.filter((h) =>
        (h.header || "").toLowerCase().includes(q)
      );
    },
    displayedRange() {
      const total = this.filteredHeaders.length;
      if (total === 0) {
        return { start: 0, end: 0, total: 0 };
      }
      return { start: 1, end: total, total };
    },
  },
  created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];

    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadHeaders();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadHeaders() {
      this.dataLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerHeaders?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.plannerCampusID,
          token: this.$store.state.userData.token,
        };

        let res = await this.get(obj);

        if (res && Array.isArray(res)) {
          this.headers = res;
        } else if (res && Array.isArray(res.data)) {
          this.headers = res.data;
        } else {
          this.headers = [];
        }
      } catch (error) {
        console.error("Error loading planner headers:", error);
        this.headers = [];
        this.$bvToast.toast("Failed to load planner headers", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    openAddModal() {
      this.isEdit = false;
      this.showModal = true;
    },

    editHeader(item) {
      this.isEdit = true;
      this.form = { ...item };
      this.showModal = true;
    },

    async deleteHeader(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerHeaders/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Planner header deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadHeaders();
        }
      }
    },

    async saveHeader() {
      if (!this.form.header.trim()) {
        this.$bvToast.toast("Please enter a title!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;
      this.form.campusID = this.plannerCampusID;

      const obj = {
        url:
          this.$store.state.domain +
          "PlannerHeaders" +
          (this.isEdit ? "/" + this.form.id : "") +
          "?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.form.campusID,
        body: this.form,
        message:
          "Planner header " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = this.isEdit ? await this.put(obj) : await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadHeaders();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        header: "",
        isActive: true,
        sequence: 0,
        campusID: this.plannerCampusID,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";
</style>
